import type { MappingReferenceData } from 'services/reference-generator/types';

const cellRefKey = (ref: MappingReferenceData['cell_references'][number]): string =>
  `${ref.ref}:${ref.direction ?? ''}`;

/** Identity of one Excel mapping so duplicates collapse without losing distinct sources. */
export const excelReferenceIdentity = (ref: MappingReferenceData): string =>
  `${ref.document_reference}|${ref.sheet_name}|${(ref.cell_references ?? []).map(cellRefKey).join(',')}`;

const withoutIdentity = (refs: MappingReferenceData[], identity: string): MappingReferenceData[] =>
  refs.filter(ref => excelReferenceIdentity(ref) !== identity);

/**
 * Drop exact-duplicate Excel mappings. First-seen identity wins; remaining
 * order is unchanged. The server has no active/history contract.
 */
export const repairExcelReferences = (refs: MappingReferenceData[] | undefined): MappingReferenceData[] => {
  if (!refs?.length) {
    return [];
  }
  const seen = new Set<string>();
  const unique: MappingReferenceData[] = [];
  refs.forEach(ref => {
    const identity = excelReferenceIdentity(ref);
    if (seen.has(identity)) {
      return;
    }
    seen.add(identity);
    unique.push(ref);
  });
  return unique;
};

/**
 * Prepend `next` when it is not already first. Drop any later copy of the same
 * identity. An exact match at index 0 is a no-op.
 */
export const replaceActiveExcelReference = (
  existing: MappingReferenceData[] | undefined,
  next: MappingReferenceData
): MappingReferenceData[] => {
  const repaired = repairExcelReferences(existing);
  const nextIdentity = excelReferenceIdentity(next);
  if (repaired[0] && excelReferenceIdentity(repaired[0]) === nextIdentity) {
    return repaired;
  }
  return [next, ...withoutIdentity(repaired, nextIdentity)];
};

export const applyProposedExcelReferences = (
  existing: MappingReferenceData[] | undefined,
  proposed: MappingReferenceData[] | undefined
): MappingReferenceData[] => {
  if (!proposed?.length) {
    return repairExcelReferences(existing);
  }
  return proposed.reduce((refs, item) => replaceActiveExcelReference(refs, item), existing ?? []);
};
